import React, { useState, useRef, useEffect } from 'react';
import { Upload, FileText, Check, X, DollarSign, CreditCard, AlertCircle, CheckCircle2, FileSpreadsheet, Settings, Key } from 'lucide-react';
import ApiService from '../services/ApiService';
import DetailedResults from './DetailedResults';

const CheckRemittanceApp = () => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [remittanceData, setRemittanceData] = useState(null);
  const [checkAmount, setCheckAmount] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [checkDate, setCheckDate] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('');
  const [matchResults, setMatchResults] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [xeroConfig, setXeroConfig] = useState({
    clientId: '',
    clientSecret: '',
    isConnected: false,
    tenantName: '',
    lastSync: null
  });
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [missingInvoiceAction, setMissingInvoiceAction] = useState('create');
  const [showValidation, setShowValidation] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const fileInputRef = useRef(null);

  // Check connection status on component mount
  useEffect(() => {
    checkConnectionStatus();
  }, []);

  // Load bank accounts when connected
  useEffect(() => {
    if (xeroConfig.isConnected) {
      loadBankAccounts();
    }
  }, [xeroConfig.isConnected]);

  const checkConnectionStatus = async () => {
    try {
      const status = await ApiService.getConnectionStatus();
      if (status.isConnected) {
        setXeroConfig(prev => ({
          ...prev,
          isConnected: true,
          tenantName: status.tenantName,
          lastSync: new Date().toISOString()
        }));
      }
    } catch (error) {
      console.error('Error checking connection status:', error);
    }
  };

  const loadBankAccounts = async () => {
    try {
      console.log('🏦 Loading bank accounts...');
      
      // First check connection status
      const connectionStatus = await ApiService.getConnectionStatus();
      console.log('Connection status:', connectionStatus);
      
      if (!connectionStatus.isConnected) {
        console.log('❌ Not connected to Xero - using mock accounts');
        console.log('📝 To see real account numbers: 1) Connect to Xero, 2) Refresh this page');
        const mockAccounts = [
          { accountID: "090", code: "090", name: "Checking Account (Mock)", bankAccountNumber: "****1234", status: "ACTIVE" },
          { accountID: "091", code: "091", name: "Savings Account (Mock)", bankAccountNumber: "****5678", status: "ACTIVE" }
        ];
        setBankAccounts(mockAccounts);
        setSelectedBankAccount(mockAccounts[0]);
        return;
      }
      
      // Try to load real bank accounts
      const accounts = await ApiService.getBankAccounts();
      console.log('✅ Bank accounts loaded from Xero:', accounts);
      
      if (Array.isArray(accounts) && accounts.length > 0) {
        setBankAccounts(accounts);
        // Auto-select the first bank account if none selected
        if (!selectedBankAccount) {
          setSelectedBankAccount(accounts[0]);
          console.log('Auto-selected bank account:', accounts[0]);
        }
      } else {
        console.log('⚠️ No bank accounts returned - using mock accounts');
        const mockAccounts = [
          { accountID: "090", code: "090", name: "Checking Account (Default)", bankAccountNumber: "****1234", status: "ACTIVE" },
          { accountID: "091", code: "091", name: "Savings Account (Default)", bankAccountNumber: "****5678", status: "ACTIVE" }
        ];
        setBankAccounts(mockAccounts);
        setSelectedBankAccount(mockAccounts[0]);
      }
    } catch (error) {
      console.error('❌ Error loading bank accounts:', error);
      console.log('🔄 Falling back to mock bank accounts...');
      
      // Set some mock bank accounts for testing
      const mockAccounts = [
        { accountID: "090", code: "090", name: "Checking Account (Fallback)", bankAccountNumber: "****1234", status: "ACTIVE" },
        { accountID: "091", code: "091", name: "Savings Account (Fallback)", bankAccountNumber: "****5678", status: "ACTIVE" }
      ];
      setBankAccounts(mockAccounts);
      setSelectedBankAccount(mockAccounts[0]);
      console.log('✅ Mock accounts set up');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFile(file);
      await processRemittanceFile(file);
    }
  };

  const processRemittanceFile = async (file) => {
    setIsProcessing(true);
    setProcessingStep('Reading remittance file...');
    
    try {
      const fileExtension = file.name.toLowerCase().split('.').pop();
      
      if (fileExtension === 'pdf') {
        await processPdfFile(file);
      } else {
        await processCsvFile(file);
      }
    } catch (error) {
      console.error('Error processing file:', error);
      setProcessingStep('Error processing file: ' + error.message);
      setIsProcessing(false);
    }
  };

  const processPdfFile = async (file) => {
    setProcessingStep('Parsing PDF remittance advice...');
    
    try {
      const formData = new FormData();
      formData.append('pdfFile', file);
      
      const response = await ApiService.parsePdf(formData);
      
      if (response.parsed) {
        // Convert PDF data to the same format as CSV
        const invoices = response.invoices.map(inv => ({
          invoiceNumber: inv.invoiceNumber,
          customerName: response.vendorName,
          amount: inv.amount,
          dueDate: inv.date,
          originalDate: inv.date,
          location: '',
          memo: inv.description,
          service: ''
        }));

        const remittanceData = {
          invoices,
          totalAmount: response.totalAmount,
          invoiceCount: response.invoiceCount,
          customerName: response.vendorName,
          fileName: file.name,
          fileType: 'PDF',
          paymentNumber: response.paymentNumber,
          checkNumber: response.checkNumber,
          checkDate: response.checkDate
        };

        setRemittanceData(remittanceData);
        
        // Auto-populate payment fields if available
        if (response.checkNumber) {
          setCheckNumber(response.checkNumber);
        }
        if (response.checkDate) {
          setCheckDate(response.checkDate);
        }
        if (response.totalAmount) {
          setCheckAmount(response.totalAmount.toString());
        }

        setProcessingStep('');
        setIsProcessing(false);
        
        // Load contacts (real if connected, mock if not)
        if (xeroConfig.isConnected) {
          await loadRealContacts(response.vendorName);
        } else {
          loadMockContacts(response.vendorName);
        }
      } else {
        throw new Error('Failed to parse PDF file');
      }
    } catch (error) {
      console.error('Error parsing PDF:', error);
      throw new Error(`PDF parsing failed: ${error.message}`);
    }
  };

  const processCsvFile = async (file) => {
    setProcessingStep('Parsing CSV file...');
    
    try {
      const fileContent = await file.text();
      const lines = fileContent.split('\n');
      const headers = lines[0].split(',').map(h => h.trim());
      const data = [];
      
      for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(',');
          const row = {};
          headers.forEach((header, index) => {
            row[header] = values[index] ? values[index].trim() : '';
          });
          
          // Support both formats: "Open Balance" (QuickBooks) and "Amount" (Guardian)
          const amount = parseFloat(row['Open Balance'] || row['Amount'] || 0);
          
          if (amount > 0) {
            data.push(row);
          }
        }
      }

      const invoices = data.map(row => ({
        invoiceNumber: row.Num || '',
        customerName: row.Name || '',
        amount: parseFloat(row['Open Balance'] || row['Amount']) || 0,
        dueDate: row['Due Date'] || '', // Optional - not used in processing
        originalDate: row.Date || '',
        location: row.Location || '',
        memo: row['Memo/Description'] || '',
        service: row.Service || ''
      }));

      const totalAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0);
      
      const remittanceData = {
        invoices,
        totalAmount,
        invoiceCount: invoices.length,
        customerName: invoices[0]?.customerName || 'Unknown',
        fileName: file.name,
        fileType: 'CSV'
      };

      setRemittanceData(remittanceData);
      setProcessingStep('File processed successfully');
      setIsProcessing(false);
      
      // Load contacts (real if connected, mock if not)
      if (xeroConfig.isConnected) {
        await loadRealContacts(invoices[0]?.customerName);
      } else {
        loadMockContacts(invoices[0]?.customerName);
      }
      
    } catch (error) {
      console.error('Error processing file:', error);
      setProcessingStep('Error processing file');
      setIsProcessing(false);
    }
  };

  const loadRealContacts = async (customerName = null) => {
    try {
      setProcessingStep('Loading contacts from Xero...');
      const xeroContacts = await ApiService.getContacts();
      
      setContacts(xeroContacts);
      
      // Auto-select matching contact if customerName provided
      if (customerName) {
        const matchingContact = xeroContacts.find(contact => 
          contact.name.toLowerCase().includes(customerName.toLowerCase()) ||
          customerName.toLowerCase().includes(contact.name.toLowerCase())
        );
        if (matchingContact) {
          setSelectedContact(matchingContact);
        }
      }
      
    } catch (error) {
      console.error('Error loading contacts:', error);
      // Fall back to mock contacts if API fails
      loadMockContacts(customerName);
    }
  };

  const loadMockContacts = (customerName) => {
    const mockContacts = [
      { 
        contactID: 'ac-ventures-123', 
        name: 'AC Ventures LLC', 
        isCustomer: true,
        accountNumber: 'CUST001'
      },
      { 
        contactID: 'maintenance-corp-456', 
        name: 'Maintenance Corp', 
        isCustomer: true,
        accountNumber: 'CUST002'
      },
      { 
        contactID: 'property-services-789', 
        name: 'Property Services Inc', 
        isCustomer: true,
        accountNumber: 'CUST003'
      }
    ];
    
    setContacts(mockContacts);
    
    // Auto-select matching contact
    const matchingContact = mockContacts.find(contact => 
      contact.name.toLowerCase().includes(customerName?.toLowerCase() || '') ||
      customerName?.toLowerCase().includes(contact.name.toLowerCase())
    );
    if (matchingContact) {
      setSelectedContact(matchingContact);
    }
  };

  const connectToXero = async () => {
    setIsProcessing(true);
    setProcessingStep('Connecting to Xero...');
    
    try {
      // Check if already connected
      const status = await ApiService.getConnectionStatus();
      if (status.isConnected) {
        setXeroConfig(prev => ({
          ...prev,
          isConnected: true,
          tenantName: status.tenantName,
          lastSync: new Date().toISOString()
        }));
        setIsProcessing(false);
        setShowSettings(false);
        alert('Already connected to Xero!');
        return;
      }

      // Get authorization URL from .NET API (uses appsettings.json credentials)
      setProcessingStep('Getting authorization URL...');
      const response = await ApiService.getAuthorizationUrl();
      const authUrl = response.authorizationUrl;
      
      setProcessingStep('Redirecting to Xero for authorization...');
      
      // Open Xero authorization in new window
      const authWindow = window.open(authUrl, 'xero-auth', 'width=600,height=700');
      
      // Listen for the callback
      const handleCallback = async (event) => {
        if (event.origin !== window.location.origin) return;
        
        if (event.data.type === 'XERO_AUTH_SUCCESS') {
          authWindow.close();
          setProcessingStep('Processing authorization...');
          
          try {
            const tokenResponse = await ApiService.exchangeCodeForToken(event.data.code, event.data.state);
            
            if (tokenResponse.success && tokenResponse.connections?.length > 0) {
              const connection = tokenResponse.connections[0];
              
              setXeroConfig(prev => ({
                ...prev,
                isConnected: true,
                tenantName: connection.tenantName,
                lastSync: new Date().toISOString()
              }));
              
              // Load real contacts
              await loadRealContacts();
              
              setIsProcessing(false);
              setShowSettings(false);
              alert('Successfully connected to Xero!');
            } else {
              // Check connection status again in case it worked
              await checkConnectionStatus();
              setIsProcessing(false);
              setShowSettings(false);
            }
          } catch (error) {
            console.error('Error during authorization:', error);
            setProcessingStep('Authorization failed');
            setIsProcessing(false);
            alert('Failed to connect to Xero. Please try again.');
          }
        }
      };
      
      window.addEventListener('message', handleCallback);
      
      // Clean up listener when window is closed
      const checkClosed = setInterval(() => {
        if (authWindow.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', handleCallback);
          if (isProcessing) {
            setIsProcessing(false);
            setProcessingStep('');
          }
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error connecting to Xero:', error);
      setProcessingStep('Connection failed');
      setIsProcessing(false);
      alert('Failed to get authorization URL. Make sure the .NET API is running.');
    }
  };

  const validateBeforeReconcile = async () => {
    if (!remittanceData || !selectedContact || !checkAmount || !checkNumber || !selectedBankAccount) {
      alert('Please complete all required fields before validation: remittance file, customer contact, check amount, check number, and bank account');
      return;
    }

    setIsProcessing(true);
    setProcessingStep('Validating payment data...');
    
    try {
      // Step 1: Search for invoices in Xero
      setProcessingStep('Validating invoices in Xero...');
      const invoiceNumbers = remittanceData.invoices.map(inv => inv.invoiceNumber);
      const searchResults = await ApiService.findInvoicesByNumbers(invoiceNumbers, selectedContact?.contactID);

      let createdCount = 0;
      let foundInvoices = [...searchResults.found];

      // Step 2: Handle missing invoices (just simulate for validation)
      if (searchResults.notFound.length > 0) {
        if (missingInvoiceAction === 'create') {
          createdCount = searchResults.notFound.length; // Simulate creation
          // Add simulated invoices to found list for validation
          searchResults.notFound.forEach(missingInvoiceNumber => {
            const remittanceInvoice = remittanceData.invoices.find(
              inv => inv.invoiceNumber === missingInvoiceNumber
            );
            if (remittanceInvoice) {
              foundInvoices.push({
                invoiceID: 'SIMULATED-' + Math.random().toString(36).substr(2, 9),
                invoiceNumber: missingInvoiceNumber,
                amountDue: remittanceInvoice.amount,
                total: remittanceInvoice.amount,
                status: 'WILL BE CREATED'
              });
            }
          });
        }
      }

      // Step 3: Validate payment structure
      setProcessingStep('Validating payment structure...');
      const paymentAmount = parseFloat(checkAmount);
      const totalAllocated = foundInvoices.reduce((sum, invoice) => {
        const remittanceInvoice = remittanceData.invoices.find(
          inv => inv.invoiceNumber === invoice.invoiceNumber
        );
        return sum + (remittanceInvoice ? remittanceInvoice.amount : 0);
      }, 0);

      // Step 4: Preview payment using dry run
      setProcessingStep('Previewing payment structure...');
      const paymentData = {
        date: checkDate || new Date().toISOString().split('T')[0],
        reference: `CHK-${checkNumber}-${new Date().toISOString().slice(0,10)}`,
        totalAmount: paymentAmount,
        contactId: selectedContact?.contactID,
        bankAccountCode: selectedBankAccount?.code || "090",
        allocations: foundInvoices.map(found => {
          const remittanceInvoice = remittanceData.invoices.find(
            inv => inv.invoiceNumber === found.invoiceNumber
          );
          
          return {
            invoiceId: found.invoiceID || found.invoice?.invoiceID,
            invoiceNumber: found.invoiceNumber,
            amount: remittanceInvoice ? remittanceInvoice.amount : 0
          };
        })
      };

      // Call dry run to validate payment structure
      const previewResults = await ApiService.previewPayment(paymentData);

      // Step 5: Compile validation results
      const validation = {
        totalInvoices: remittanceData.invoiceCount,
        foundInvoices: searchResults.found.length,
        missingInvoices: searchResults.notFound.length,
        willCreateInvoices: missingInvoiceAction === 'create' ? searchResults.notFound.length : 0,
        totalRemittanceAmount: remittanceData.totalAmount,
        checkAmount: paymentAmount,
        totalAllocated: totalAllocated,
        amountDifference: paymentAmount - totalAllocated,
        isBalanced: Math.abs(paymentAmount - totalAllocated) < 0.01,
        paymentReference: paymentData.reference,
        contactName: selectedContact.name,
        invoiceDetails: foundInvoices.map(invoice => {
          const remittanceInvoice = remittanceData.invoices.find(
            inv => inv.invoiceNumber === invoice.invoiceNumber
          );
          return {
            invoiceNumber: invoice.invoiceNumber,
            remittanceAmount: remittanceInvoice?.amount || 0,
            xeroDueAmount: invoice.amountDue || invoice.total || 0,
            status: invoice.status || 'FOUND',
            matched: Math.abs((remittanceInvoice?.amount || 0) - (invoice.amountDue || invoice.total || 0)) < 0.01
          };
        }),
        missingInvoicesList: searchResults.notFound,
        previewResults: previewResults,
        issues: []
      };

      // Add validation issues
      if (!validation.isBalanced) {
        validation.issues.push(`Amount mismatch: Check amount $${paymentAmount.toFixed(2)} vs allocated $${totalAllocated.toFixed(2)}`);
      }
      if (searchResults.notFound.length > 0 && missingInvoiceAction === 'skip') {
        validation.issues.push(`${searchResults.notFound.length} invoices not found and will be skipped`);
      }
      validation.invoiceDetails.forEach(invoice => {
        if (!invoice.matched) {
          validation.issues.push(`Amount mismatch for ${invoice.invoiceNumber}: Remittance $${invoice.remittanceAmount.toFixed(2)} vs Xero $${invoice.xeroDueAmount.toFixed(2)}`);
        }
      });

      setValidationResults(validation);
      setShowValidation(true);
      
    } catch (error) {
      console.error('Error during validation:', error);
      alert(`Validation failed: ${error.message}`);
    } finally {
      setIsProcessing(false);
      setProcessingStep('');
    }
  };

  const reconcileWithXero = async () => {
    if (!xeroConfig.isConnected) {
      alert('Please connect to Xero first');
      setShowSettings(true);
      return;
    }

    if (!selectedContact) {
      alert('Please select a customer contact first');
      return;
    }

    if (!selectedBankAccount) {
      alert('Please select a bank account for the check deposit');
      return;
    }

    if (!remittanceData || !checkAmount || !checkNumber) {
      alert('Please fill in all check details and upload remittance file');
      return;
    }

    const paymentAmount = parseFloat(checkAmount);
    if (Math.abs(paymentAmount - remittanceData.totalAmount) > 0.01) {
      if (!window.confirm(`Check amount ($${paymentAmount.toFixed(2)}) doesn't match remittance total ($${remittanceData.totalAmount.toFixed(2)}). Continue anyway?`)) {
        return;
      }
    }

    setIsProcessing(true);
    
    try {
      // Step 1: Search for invoices in Xero
      setProcessingStep('Searching for invoices in Xero...');
      const invoiceNumbers = remittanceData.invoices.map(inv => inv.invoiceNumber);
      const searchResults = await ApiService.findInvoicesByNumbers(invoiceNumbers, selectedContact?.contactID);

      let createdCount = 0;
      let foundInvoices = [...searchResults.found];

      // Step 2: Handle missing invoices - CREATE ALL FIRST
      let failedInvoiceCreations = [];
      if (searchResults.notFound.length > 0) {
        if (missingInvoiceAction === 'create') {
          setProcessingStep(`Creating ${searchResults.notFound.length} missing invoices...`);
          
          // Create all missing invoices first, track failures
          for (const missingInvoiceNumber of searchResults.notFound) {
            try {
              const remittanceInvoice = remittanceData.invoices.find(
                inv => inv.invoiceNumber === missingInvoiceNumber
              );
              
              if (remittanceInvoice) {
                setProcessingStep(`Creating invoice ${missingInvoiceNumber}...`);
                // Ensure we have valid dates
                const invoiceDate = remittanceInvoice.originalDate || new Date().toISOString().split('T')[0];
                const dueDateValue = remittanceInvoice.dueDate || 
                  new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // 30 days from now
                
                console.log(`📅 Invoice ${missingInvoiceNumber} dates: invoice=${invoiceDate}, due=${dueDateValue}`);
                
                const createdInvoice = await ApiService.createInvoice({
                  contactId: selectedContact.contactID,
                  invoiceNumber: missingInvoiceNumber,
                  amount: remittanceInvoice.amount,
                  date: invoiceDate,
                  dueDate: dueDateValue,
                  description: remittanceInvoice.memo || remittanceInvoice.service,
                  reference: remittanceInvoice.memo
                });
                
                // Validate the created invoice response
                if (!createdInvoice.invoiceId || !createdInvoice.amountDue) {
                  throw new Error(`Invalid invoice creation response: missing invoiceId or amountDue`);
                }
                
                // Validate invoice ID is a proper GUID
                const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                if (!guidRegex.test(createdInvoice.invoiceId)) {
                  throw new Error(`Invalid invoice ID format: ${createdInvoice.invoiceId}`);
                }
                
                foundInvoices.push({
                  invoiceID: createdInvoice.invoiceId,
                  invoiceNumber: missingInvoiceNumber,
                  amountDue: createdInvoice.amountDue,
                  total: createdInvoice.amountDue
                });
                createdCount++;
                console.log(`✅ Successfully created invoice ${missingInvoiceNumber} with ID: ${createdInvoice.invoiceId}`);
              }
            } catch (error) {
              console.error(`❌ Failed to create invoice ${missingInvoiceNumber}:`, error);
              
              // Check if error is because invoice already exists
              const errorMsg = error.message || '';
              if (errorMsg.includes('payments or credit notes allocated') || 
                  errorMsg.includes('LineItemID') ||
                  errorMsg.includes('Invoice not of valid status for modification')) {
                console.warn(`⚠️ Invoice ${missingInvoiceNumber} already exists in Xero - attempting to find and use existing invoice`);
                
                // Try to search for the existing invoice
                try {
                  const searchResult = await ApiService.findInvoicesByNumbers([missingInvoiceNumber], selectedContact?.contactID);
                  
                  if (searchResult.found && searchResult.found.length > 0) {
                    const existingInvoice = searchResult.found[0];
                    console.log(`✅ Found existing invoice ${missingInvoiceNumber} with ID: ${existingInvoice.invoiceID}`);
                    
                    // Add to foundInvoices so it can be used in payment
                    foundInvoices.push({
                      invoiceID: existingInvoice.invoiceID,
                      invoiceNumber: missingInvoiceNumber,
                      amountDue: existingInvoice.amountDue || remittanceInvoice.amount,
                      total: existingInvoice.total || remittanceInvoice.amount
                    });
                  } else {
                    console.warn(`⚠️ Could not find existing invoice ${missingInvoiceNumber} - it may be fully paid already`);
                  }
                } catch (searchError) {
                  console.error(`❌ Error searching for existing invoice ${missingInvoiceNumber}:`, searchError);
                }
              } else {
                // Real error - add to failures
                failedInvoiceCreations.push({
                  invoiceNumber: missingInvoiceNumber,
                  error: error.message
                });
              }
            }
          }
          
          // Debug logging after all invoice creation attempts
          console.log(`📊 Invoice creation summary:`);
          console.log(`  • Total invoices to create: ${searchResults.notFound.length}`);
          console.log(`  • Successfully created: ${createdCount}`);
          console.log(`  • Failed to create: ${failedInvoiceCreations.length}`);
          console.log(`  • Total found/created invoices: ${foundInvoices.length}`);
          console.log(`  • Found invoices:`, foundInvoices.map(inv => `${inv.invoiceNumber} (${inv.invoiceID})`));
          
          // Check if any invoice creations failed
          if (failedInvoiceCreations.length > 0) {
            const errorMessage = `Failed to create ${failedInvoiceCreations.length} invoices:\n` +
              failedInvoiceCreations.map(f => `• ${f.invoiceNumber}: ${f.error}`).join('\n');
            
            console.error('❌ Invoice creation failures:', failedInvoiceCreations);
            setProcessingStep(`Failed to create ${failedInvoiceCreations.length} invoices`);
            
            // If ALL invoice creations failed, stop immediately
            if (foundInvoices.length === 0) {
              alert(`❌ All invoice creations failed!\n\n${errorMessage}\n\nCannot proceed with payment processing.`);
              setProcessingStep('Payment processing stopped - all invoice creations failed');
              setIsProcessing(false);
              return;
            }
            
            // If some succeeded, ask user if they want to continue with partial success
            const continueAnyway = confirm(
              `⚠️ INVOICE CREATION FAILURES:\n\n${errorMessage}\n\n✅ Successfully created/found: ${foundInvoices.length} invoices\n\nDo you want to continue with payment processing for the successful invoices only?`
            );
            
            if (!continueAnyway) {
              console.log('🛑 User chose to cancel payment processing due to invoice creation failures');
              setProcessingStep('Payment processing cancelled by user due to invoice creation failures');
              setIsProcessing(false);
              return;
            }
            
            console.log('⚠️ User chose to continue with partial success - proceeding with payment processing');
            setProcessingStep(`Continuing with ${foundInvoices.length} successful invoices...`);
          }
        }
      }

      // Step 3: Check for amount mismatches and auto-update invoices if remittance is higher
      console.log(`🔍 Checking for amount mismatches in ${foundInvoices.length} invoices`);
      setProcessingStep('Checking invoice amounts...');
      
      if (foundInvoices.length === 0) {
        console.error('🚫 No invoices available for payment processing');
        setProcessingStep('No invoices available for payment processing');
        setIsProcessing(false);
        return;
      }

      // Check each invoice for amount mismatches
      const invoicesToUpdate = [];
      for (const invoice of foundInvoices) {
        const remittanceInvoice = remittanceData.invoices.find(
          inv => inv.invoiceNumber === invoice.invoiceNumber
        );
        
        if (remittanceInvoice) {
          const remittanceAmount = remittanceInvoice.amount;
          const xeroAmount = invoice.amountDue || invoice.total || 0;
          const difference = remittanceAmount - xeroAmount;
          
          // If remittance amount is higher, we need to update the invoice
          if (difference > 0.01) {
            invoicesToUpdate.push({
              invoiceId: invoice.invoiceID,
              invoiceNumber: invoice.invoiceNumber,
              currentAmount: xeroAmount,
              newAmount: remittanceAmount,
              difference: difference
            });
          }
        }
      }

      // If there are invoices to update, handle them BEFORE allocations
      if (invoicesToUpdate.length > 0) {
        const updateMessage = `Found ${invoicesToUpdate.length} invoice(s) where remittance amount is HIGHER than Xero:\n\n` +
          invoicesToUpdate.map(inv =>
            `• ${inv.invoiceNumber}: Xero $${inv.currentAmount.toFixed(2)} → Remittance $${inv.newAmount.toFixed(2)} (+$${inv.difference.toFixed(2)})`
          ).join('\n') +
          `\n\nDo you want to UPDATE these invoices in Xero before processing the payment?`;

        const shouldUpdate = confirm(updateMessage);

        if (shouldUpdate) {
          setProcessingStep(`Updating ${invoicesToUpdate.length} invoice(s) in Xero...`);

          let updateSuccessCount = 0;
          const updateFailures = [];

          for (const invoiceToUpdate of invoicesToUpdate) {
            try {
              console.log(`📝 Updating invoice ${invoiceToUpdate.invoiceNumber} from $${invoiceToUpdate.currentAmount} to $${invoiceToUpdate.newAmount}`);

              await ApiService.updateInvoice(invoiceToUpdate.invoiceId, { total: invoiceToUpdate.newAmount });

              // Reflect the updated amount in foundInvoices so allocations use the new value
              const invoiceIndex = foundInvoices.findIndex(inv => inv.invoiceID === invoiceToUpdate.invoiceId);
              if (invoiceIndex !== -1) {
                foundInvoices[invoiceIndex].amountDue = invoiceToUpdate.newAmount;
                foundInvoices[invoiceIndex].total = invoiceToUpdate.newAmount;
              }

              updateSuccessCount++;
              console.log(`✅ Successfully updated invoice ${invoiceToUpdate.invoiceNumber}`);
            } catch (error) {
              console.error(`❌ Failed to update invoice ${invoiceToUpdate.invoiceNumber}:`, error);
              updateFailures.push({ number: invoiceToUpdate.invoiceNumber, reason: error.message || 'Unknown error' });
            }
          }

          if (updateFailures.length > 0) {
            // Some updates failed — stop before allocations and let the user decide
            const failDetails = updateFailures.map(f => `• ${f.number}: ${f.reason}`).join('\n');
            const proceedAnyway = confirm(
              `⚠️ Updated ${updateSuccessCount} invoice(s), but ${updateFailures.length} could not be updated:\n\n` +
              `${failDetails}\n\n` +
              `The payment allocations for these invoices will use the CURRENT Xero amounts (which are LOWER than the remittance).\n\n` +
              `Click OK to proceed anyway, or Cancel to stop and fix the invoices in Xero first.`
            );

            if (!proceedAnyway) {
              setProcessingStep('Payment cancelled — please update the invoices in Xero and retry.');
              setIsProcessing(false);
              return;
            }
            console.log(`⚠️ User chose to proceed with ${updateFailures.length} unupdated invoice(s)`);
          } else {
            console.log(`✅ All ${updateSuccessCount} invoice(s) updated — proceeding to allocations`);
          }
        } else {
          // User declined to update — confirm they want to allocate with the lower amounts
          const proceedWithLower = confirm(
            `⚠️ The following invoice amounts in Xero are LOWER than the remittance:\n\n` +
            invoicesToUpdate.map(inv =>
              `• ${inv.invoiceNumber}: Xero $${inv.currentAmount.toFixed(2)} vs Remittance $${inv.newAmount.toFixed(2)}`
            ).join('\n') +
            `\n\nPayment will be allocated using the current (lower) Xero amounts.\n\nProceed?`
          );

          if (!proceedWithLower) {
            setProcessingStep('Payment cancelled — please update the invoices in Xero and retry.');
            setIsProcessing(false);
            return;
          }
          console.log('⚠️ User chose to proceed with current Xero amounts without updating');
        }
      }
      
      // Validate all invoice IDs are proper GUIDs
      const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const invalidInvoices = foundInvoices.filter(inv => 
        !inv.invoiceID || !guidRegex.test(inv.invoiceID)
      );
      
      if (invalidInvoices.length > 0) {
        const errorMessage = `Found ${invalidInvoices.length} invoices with invalid IDs:\n` +
          invalidInvoices.map(inv => `• ${inv.invoiceNumber}: ${inv.invoiceID || 'missing'}`).join('\n');
        
        console.error('Invalid invoice IDs found:', invalidInvoices);
        alert(`Cannot proceed with payment processing:\n\n${errorMessage}\n\nPlease check the invoice creation process.`);
        setProcessingStep('Payment processing failed due to invalid invoice IDs');
        setIsProcessing(false);
        return;
      }
      
      // Validate we have amounts for all invoices
      const missingAmounts = foundInvoices.filter(inv => !inv.amountDue && !inv.total);
      if (missingAmounts.length > 0) {
        const errorMessage = `Found ${missingAmounts.length} invoices with missing amounts:\n` +
          missingAmounts.map(inv => `• ${inv.invoiceNumber}`).join('\n');
        
        console.error('Missing amounts found:', missingAmounts);
        alert(`Cannot proceed with payment processing:\n\n${errorMessage}\n\nPlease check the invoice data.`);
        setProcessingStep('Payment processing failed due to missing invoice amounts');
        setIsProcessing(false);
        return;
      }
      
      console.log(`✅ All ${foundInvoices.length} invoices validated successfully`);
      
      // Use all validated invoices for payment (skip the expensive per-invoice API validation)
      const payableInvoices = foundInvoices;
      
      console.log(`💰 Proceeding with payment for ${payableInvoices.length} invoices`);
      
      // Step 4: Create single payment with allocations
      setProcessingStep('Creating payment with allocations in Xero...');
      
      const paymentData = {
        date: checkDate || new Date().toISOString().split('T')[0],
        reference: `CHK-${checkNumber}-${new Date().toISOString().slice(0,10)}`,
        totalAmount: paymentAmount,
        contactId: selectedContact?.contactID,
        bankAccountCode: selectedBankAccount?.code || "090",
        allocations: payableInvoices.map(found => {
          const remittanceInvoice = remittanceData.invoices.find(
            inv => inv.invoiceNumber === found.invoiceNumber
          );
          
          return {
            invoiceId: found.invoiceID,
            invoiceNumber: found.invoiceNumber,
            amount: remittanceInvoice ? remittanceInvoice.amount : (found.amountDue || found.total || 0)
          };
        })
      };

      console.log(`💰 Creating payment for $${paymentAmount} across ${payableInvoices.length} invoices:`, 
        paymentData.allocations.map(a => `${a.invoiceNumber}: $${a.amount}`).join(', '));

      const paymentResults = await ApiService.createSinglePayment(paymentData);
      
      // Store detailed results for display
      setMatchResults({
        totalInvoices: remittanceData.invoiceCount,
        successfulInvoices: paymentResults.paymentsCreated,
        notFoundCount: searchResults.notFound.length - createdCount,
        createdCount,
        failedInvoiceCreations: failedInvoiceCreations.length,
        amountMismatchCount: 0,
        totalAmount: remittanceData.totalAmount,
        paymentAmount: paymentAmount,
        successRate: paymentResults.summary?.successRate || 0,
        paymentReference: `CHK-${checkNumber}-${new Date().toISOString().slice(0,10)}`,
        hasIssues: paymentResults.paymentsFailed > 0 || failedInvoiceCreations.length > 0,
        // Add detailed results
        detailedResults: paymentResults,
        invoiceCreationErrors: failedInvoiceCreations
      });

      setProcessingStep('Reconciliation completed!');
      
    } catch (error) {
      console.error('Error during reconciliation:', error);
      setProcessingStep('Reconciliation failed');
      alert(`Reconciliation failed: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const resetForm = () => {
    setUploadedFile(null);
    setRemittanceData(null);
    setCheckAmount('');
    setCheckNumber('');
    setCheckDate('');
    setMatchResults(null);
    setSelectedContact(null);
    setProcessingStep('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-100 p-6">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-blue-600 px-8 py-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                  <CreditCard className="w-8 h-8" />
                  Check Remittance Reconciliation
                </h1>
                <p className="text-green-100 mt-2">Upload remittance file and reconcile payment against multiple Xero invoices</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-white">
                  <div className={`w-3 h-3 rounded-full ${xeroConfig.isConnected ? 'bg-green-400' : 'bg-red-400'}`}></div>
                  <span className="text-sm">
                    {xeroConfig.isConnected 
                      ? `Connected: ${xeroConfig.tenantName}` 
                      : 'Not Connected'
                    }
                  </span>
                </div>
                <button
                  onClick={() => setShowSettings(true)}
                  className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-lg transition-colors duration-200"
                >
                  <Settings className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          <div className="p-8">
            {/* Settings Modal */}
            {showSettings && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                      <Key className="w-6 h-6" />
                      Xero Configuration
                    </h2>
                    <button
                      onClick={() => setShowSettings(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>

                  <div className="space-y-6">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h3 className="font-semibold text-blue-800 mb-2">Xero Integration</h3>
                      <div className="text-sm text-blue-700 space-y-2">
                        <p>✅ <strong>API Credentials</strong>: Configured in appsettings.json</p>
                        <p>✅ <strong>Callback URL</strong>: https://localhost:7080/api/xero/callback</p>
                        <p>✅ <strong>.NET API</strong>: Running and ready</p>
                        <p>🔗 <strong>Ready to Connect</strong>: Click "Connect to Xero" below</p>
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-lg p-4">
                      <h4 className="font-semibold text-green-800 mb-2">How It Works</h4>
                      <ol className="text-sm text-green-700 space-y-1">
                        <li>1. Your .NET API uses credentials from appsettings.json</li>
                        <li>2. Click "Connect to Xero" to start OAuth flow</li>
                        <li>3. Sign in to your Xero account</li>
                        <li>4. Authorize the connection</li>
                        <li>5. Start processing payments!</li>
                      </ol>
                    </div>

                    {xeroConfig.isConnected && (
                      <div className="bg-green-50 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-green-800 mb-2">
                          <CheckCircle2 className="w-5 h-5" />
                          <span className="font-semibold">Connected to Xero</span>
                        </div>
                        <div className="text-sm text-green-700">
                          <p><strong>Organization:</strong> {xeroConfig.tenantName}</p>
                          <p><strong>Connected:</strong> {new Date(xeroConfig.lastSync).toLocaleString()}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex gap-4">
                      <button
                        onClick={connectToXero}
                        disabled={isProcessing}
                        className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
                      >
                        {isProcessing ? 'Connecting...' : 'Connect to Xero'}
                      </button>
                      <button
                        onClick={() => setShowSettings(false)}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors duration-200"
                      >
                        Close
                      </button>
                    </div>

                    {isProcessing && processingStep && (
                      <div className="text-center py-2">
                        <p className="text-gray-600">{processingStep}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {!matchResults ? (
              <div className="space-y-8">
                {/* Step 1: Upload */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <FileSpreadsheet className="w-5 h-5" />
                    Step 1: Upload Remittance File
                  </h2>
                  
                  {!uploadedFile ? (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-green-400 hover:bg-green-50 transition-all duration-200"
                    >
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-2">Click to upload remittance file</p>
                      <p className="text-sm text-gray-500">Support: CSV files (.csv) or PDF remittance advice (.pdf)</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.txt,.pdf"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                    </div>
                  ) : (
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <FileText className="w-6 h-6 text-green-600" />
                          <div>
                            <p className="font-medium text-green-800">{uploadedFile.name}</p>
                            <p className="text-sm text-green-600">File uploaded successfully</p>
                          </div>
                        </div>
                        <button
                          onClick={resetForm}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}

                  {isProcessing && !remittanceData && (
                    <div className="mt-4 text-center py-4">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                      <p className="text-gray-600 mt-2">{processingStep}</p>
                    </div>
                  )}
                </div>

                {/* Step 2: Contact Selection */}
                {remittanceData && (
                  <div className="bg-purple-50 rounded-xl p-6">
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-purple-800">
                      <FileText className="w-5 h-5" />
                      Step 2: Select Customer Contact
                    </h2>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Customer Contact
                        </label>
                        <select
                          value={selectedContact?.contactID || ''}
                          onChange={(e) => {
                            const contact = contacts.find(c => c.contactID === e.target.value);
                            setSelectedContact(contact);
                          }}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                          <option value="">Select a customer contact...</option>
                          {contacts.filter(c => c.isCustomer).map(contact => (
                            <option key={contact.contactID} value={contact.contactID}>
                              {contact.name} ({contact.accountNumber})
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {selectedContact && (
                        <div className="bg-white rounded-lg p-4 border border-purple-200">
                          <h4 className="font-medium text-purple-800 mb-2">Selected Contact</h4>
                          <div className="text-sm text-gray-700 space-y-1">
                            <p><strong>Name:</strong> {selectedContact.name}</p>
                            <p><strong>Account:</strong> {selectedContact.accountNumber}</p>
                          </div>
                        </div>
                      )}

                      {/* Bank Account Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          💳 Deposit Into Bank Account
                        </label>
                        <select
                          value={selectedBankAccount?.code || ''}
                          onChange={(e) => {
                            const account = bankAccounts.find(a => a.code === e.target.value);
                            setSelectedBankAccount(account);
                          }}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        >
                          <option value="">Select bank account for check deposit...</option>
                          {bankAccounts.map(account => (
                            <option key={account.code} value={account.code}>
                              {account.name} ({account.code}) {account.bankAccountNumber ? `- ${account.bankAccountNumber}` : ''}
                            </option>
                          ))}
                        </select>
                        {bankAccounts.length === 0 && (
                          <p className="text-sm text-gray-500 mt-1">Loading bank accounts...</p>
                        )}
                      </div>

                      {selectedBankAccount && (
                        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                          <h4 className="font-medium text-green-800 mb-2">✅ Check Will Be Deposited Into</h4>
                          <div className="text-sm text-gray-700 space-y-1">
                            <p><strong>Account:</strong> {selectedBankAccount.name}</p>
                            <p><strong>Account Code:</strong> {selectedBankAccount.code}</p>
                            {selectedBankAccount.bankAccountNumber && (
                              <p><strong>Account Number:</strong> {selectedBankAccount.bankAccountNumber}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 3: Summary */}
                {remittanceData && selectedContact && (
                  <div className="bg-blue-50 rounded-xl p-6">
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-blue-800">
                      <FileText className="w-5 h-5" />
                      Step 3: Remittance Summary
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-blue-600">{remittanceData.invoiceCount}</p>
                        <p className="text-sm text-gray-600">Invoices</p>
                      </div>
                      <div className="bg-white rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-green-600">${remittanceData.totalAmount.toFixed(2)}</p>
                        <p className="text-sm text-gray-600">Total Amount</p>
                      </div>
                      <div className="bg-white rounded-lg p-4 text-center">
                        <p className="text-lg font-bold text-gray-700">{selectedContact.name}</p>
                        <p className="text-sm text-gray-600">Customer</p>
                      </div>
                      <div className="bg-white rounded-lg p-4 text-center">
                        <p className="text-lg font-bold text-gray-700">{remittanceData.fileName}</p>
                        <p className="text-sm text-gray-600">File</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 4: Missing Invoice Handling */}
                {remittanceData && selectedContact && (
                  <div className="bg-orange-50 rounded-xl p-6">
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-orange-800">
                      <AlertCircle className="w-5 h-5" />
                      Step 4: Handle Missing Invoices
                    </h2>
                    
                    <div className="space-y-3">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="missingInvoiceAction"
                          value="report"
                          checked={missingInvoiceAction === 'report'}
                          onChange={(e) => setMissingInvoiceAction(e.target.value)}
                          className="mt-1"
                        />
                        <div>
                          <div className="font-medium text-gray-800">Report Only</div>
                          <div className="text-sm text-gray-600">List missing invoices for manual review</div>
                        </div>
                      </label>
                      
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="missingInvoiceAction"
                          value="create"
                          checked={missingInvoiceAction === 'create'}
                          onChange={(e) => setMissingInvoiceAction(e.target.value)}
                          className="mt-1"
                        />
                        <div>
                          <div className="font-medium text-gray-800">Auto-Create Invoices</div>
                          <div className="text-sm text-gray-600">Create missing invoices in Xero automatically</div>
                        </div>
                      </label>
                      
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="missingInvoiceAction"
                          value="skip"
                          checked={missingInvoiceAction === 'skip'}
                          onChange={(e) => setMissingInvoiceAction(e.target.value)}
                          className="mt-1"
                        />
                        <div>
                          <div className="font-medium text-gray-800">Skip Missing</div>
                          <div className="text-sm text-gray-600">Apply payment only to existing invoices</div>
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                {/* Step 5: Check Details */}
                {remittanceData && selectedContact && (
                  <div className="bg-yellow-50 rounded-xl p-6">
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-yellow-800">
                      <DollarSign className="w-5 h-5" />
                      Step 5: Enter Check Details
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Check Amount
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={checkAmount}
                          onChange={(e) => setCheckAmount(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          placeholder="Enter check amount"
                        />
                        {checkAmount && remittanceData && (
                          <p className={`text-sm mt-1 ${Math.abs(parseFloat(checkAmount) - remittanceData.totalAmount) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                            {Math.abs(parseFloat(checkAmount) - remittanceData.totalAmount) < 0.01 
                              ? '✓ Matches remittance total' 
                              : `Difference: $${Math.abs(parseFloat(checkAmount) - remittanceData.totalAmount).toFixed(2)}`
                            }
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Check Number
                        </label>
                        <input
                          type="text"
                          value={checkNumber}
                          onChange={(e) => setCheckNumber(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          placeholder="Enter check number"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Check Date
                        </label>
                        <input
                          type="date"
                          value={checkDate}
                          onChange={(e) => setCheckDate(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 6: Validate & Reconcile */}
                {remittanceData && selectedContact && checkAmount && checkNumber && (
                  <div className="text-center space-y-4">
                    <div className="flex gap-4 justify-center">
                      <button
                        onClick={validateBeforeReconcile}
                        disabled={isProcessing}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-3 px-6 rounded-lg transition-colors flex items-center gap-2"
                      >
                        {isProcessing ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            Validating...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-5 h-5" />
                            Validate Payment
                          </>
                        )}
                      </button>
                      
                      {validationResults && validationResults.issues.length === 0 && (
                        <button
                          onClick={reconcileWithXero}
                          disabled={isProcessing}
                          className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold py-3 px-6 rounded-lg transition-colors flex items-center gap-2"
                        >
                          {isProcessing ? (
                            <>
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                              Processing...
                            </>
                          ) : (
                            <>
                              <Check className="w-5 h-5" />
                              Execute Payment
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    
                    {isProcessing && (
                      <p className="text-gray-600">{processingStep}</p>
                    )}
                    
                    {validationResults && validationResults.issues.length > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <h4 className="font-semibold text-red-800 mb-2">⚠️ Validation Issues Found</h4>
                        <p className="text-red-700 text-sm mb-2">Please resolve these issues before proceeding:</p>
                        <ul className="text-red-700 text-sm list-disc list-inside space-y-1">
                          {validationResults.issues.map((issue, index) => (
                            <li key={index}>{issue}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              /* Results Screen */
              <div className="space-y-6">
                <div className="text-center">
                  <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">Reconciliation Complete!</h2>
                  <p className="text-gray-600">Payment has been successfully applied to Xero invoices</p>
                </div>

                {/* Results Summary */}
                <div className="bg-green-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4 text-green-800">Payment Processing Results</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                    <div className="bg-white rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-green-600">{matchResults.successfulInvoices}</p>
                      <p className="text-sm text-gray-600">Successfully Applied</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-red-600">{matchResults.notFoundCount}</p>
                      <p className="text-sm text-gray-600">Not Found</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-blue-600">{matchResults.createdCount || 0}</p>
                      <p className="text-sm text-gray-600">Auto-Created</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-orange-600">{matchResults.amountMismatchCount}</p>
                      <p className="text-sm text-gray-600">Mismatches</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-purple-600">{matchResults.successRate}%</p>
                      <p className="text-sm text-gray-600">Success Rate</p>
                    </div>
                  </div>
                  
                  <div className="bg-blue-100 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2">Single Payment Record Created</h4>
                    <div className="text-sm text-blue-700 space-y-1">
                      <p><strong>Payment Reference:</strong> {matchResults.paymentReference}</p>
                      <p><strong>Amount:</strong> ${matchResults.paymentAmount.toFixed(2)}</p>
                      <p><strong>Check Number:</strong> {checkNumber}</p>
                      <p><strong>Customer:</strong> {selectedContact.name}</p>
                      <p><strong>Applied to:</strong> {matchResults.successfulInvoices} invoices</p>
                    </div>
                  </div>
                </div>

                {/* Detailed Results */}
                {matchResults.detailedResults && (
                  <DetailedResults results={matchResults.detailedResults} />
                )}

                {/* Actions */}
                <div className="flex gap-4 justify-center">
                  <button
                    onClick={resetForm}
                    className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors duration-200"
                  >
                    Process Another Payment
                  </button>
                  <button
                    onClick={() => alert('Opening Xero dashboard...')}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                  >
                    View in Xero
                  </button>
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="mt-8 bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-3">How This Works</h3>
              <ol className="text-sm text-gray-700 space-y-2">
                <li>1. <strong>Configure Xero:</strong> Set up API credentials and connect</li>
                <li>2. <strong>Upload remittance:</strong> CSV file with invoice details</li>
                <li>3. <strong>Select customer:</strong> Choose correct contact from Xero</li>
                <li>4. <strong>Select bank account:</strong> Choose where the check will be deposited</li>
                <li>5. <strong>Review summary:</strong> Verify invoice count and amounts</li>
                <li>6. <strong>Handle missing:</strong> Choose how to deal with invoices not found</li>
                <li>7. <strong>Enter check details:</strong> Amount, number, and date</li>
                <li>8. <strong>Reconcile:</strong> Create single payment with allocations</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* Validation Results Modal */}
      {showValidation && validationResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Payment Validation Results</h2>
                <button
                  onClick={() => setShowValidation(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-blue-800">Invoice Summary</h3>
                  <p className="text-sm text-blue-700">Found: {validationResults.foundInvoices}</p>
                  <p className="text-sm text-blue-700">Missing: {validationResults.missingInvoices}</p>
                  <p className="text-sm text-blue-700">Will Create: {validationResults.willCreateInvoices}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-green-800">Amount Summary</h3>
                  <p className="text-sm text-green-700">Check: ${validationResults.checkAmount.toFixed(2)}</p>
                  <p className="text-sm text-green-700">Allocated: ${validationResults.totalAllocated.toFixed(2)}</p>
                  <p className="text-sm text-green-700">Difference: ${validationResults.amountDifference.toFixed(2)}</p>
                </div>
                <div className={`p-4 rounded-lg ${validationResults.issues.length === 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                  <h3 className={`font-semibold ${validationResults.issues.length === 0 ? 'text-green-800' : 'text-red-800'}`}>
                    Validation Status
                  </h3>
                  <p className={`text-sm ${validationResults.issues.length === 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {validationResults.issues.length === 0 ? '✅ Ready to process' : `❌ ${validationResults.issues.length} issues found`}
                  </p>
                </div>
              </div>

              {/* Issues */}
              {validationResults.issues.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold text-red-800 mb-3">Issues to Resolve:</h3>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <ul className="space-y-2">
                      {validationResults.issues.map((issue, index) => (
                        <li key={index} className="text-red-700 flex items-start gap-2">
                          <span className="text-red-500 mt-1">•</span>
                          <span className="text-sm">{issue}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Invoice Details */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-800 mb-3">Invoice Details:</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Invoice #</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Status</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700">Remittance</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700">Xero Due</th>
                        <th className="px-4 py-2 text-center text-sm font-semibold text-gray-700">Match</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validationResults.invoiceDetails.map((invoice, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-2 text-sm text-gray-900">{invoice.invoiceNumber}</td>
                          <td className="px-4 py-2 text-sm">
                            <span className={`px-2 py-1 rounded text-xs ${
                              invoice.status === 'WILL BE CREATED' ? 'bg-blue-100 text-blue-800' :
                              invoice.status === 'FOUND' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {invoice.status}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 text-right">${invoice.remittanceAmount.toFixed(2)}</td>
                          <td className="px-4 py-2 text-sm text-gray-900 text-right">${invoice.xeroDueAmount.toFixed(2)}</td>
                          <td className="px-4 py-2 text-center">
                            {invoice.matched ? (
                              <span className="text-green-600">✅</span>
                            ) : (
                              <span className="text-red-600">❌</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowValidation(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
                {validationResults.issues.length === 0 ? (
                  <button
                    onClick={() => {
                      setShowValidation(false);
                      reconcileWithXero();
                    }}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Proceed with Payment
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setShowValidation(false);
                      reconcileWithXero();
                    }}
                    className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                  >
                    Override Issues & Proceed
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckRemittanceApp;

